import { Platform } from 'react-native';

import { SITE } from '@site';

// Which AI assistant a visit came from, sent as the `via` property of the
// Umami `affiliate-click` event (open-external.ts) so clicks — and the
// commissions behind them — can be split by AI origin.
//
// ChatGPT tags its links with `utm_source=chatgpt.com`; the others only show up
// as the referrer. Both are only visible on the visit's landing page: a later
// full page load has our own site as its referrer and no utm. So the landing
// page stores what it found in sessionStorage (rememberAiReferral, called once
// by the root layout), and every click reads it back. Web only; storage can
// throw or be empty (private mode, blocked site data), which just means no
// `via` property.

const STORAGE_KEY = `${SITE.id}:ai-via`;

// Host (or utm_source) → the label sent to Umami. Matched on the hostname and
// its parent domains, so `www.perplexity.ai` hits `perplexity.ai`.
const AI_SOURCES: Record<string, string> = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'gemini.google.com': 'gemini',
  'bard.google.com': 'gemini',
  'claude.ai': 'claude',
  'copilot.microsoft.com': 'copilot',
  'chat.mistral.ai': 'mistral',
  'chat.deepseek.com': 'deepseek',
  'grok.com': 'grok',
  'meta.ai': 'meta-ai',
  'you.com': 'you',
};

function aiSourceForHost(host: string): string | null {
  const parts = host.toLowerCase().replace(/\.$/, '').split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    const label = AI_SOURCES[parts.slice(i).join('.')];
    if (label) return label;
  }
  return null;
}

/** The AI assistant this page load came from, if any (utm_source first, then the referrer). */
function detectAiReferral(): string | null {
  try {
    const utm = new URLSearchParams(window.location.search).get('utm_source');
    if (utm) {
      const label = aiSourceForHost(utm) ?? AI_SOURCES[utm.toLowerCase()];
      if (label) return label;
    }
    if (document.referrer) return aiSourceForHost(new URL(document.referrer).hostname);
  } catch {
    // Malformed referrer / URL — treat as no AI origin.
  }
  return null;
}

/**
 * Stores the landing page's AI origin for the rest of the visit (the tab's
 * sessionStorage). A later AI landing in the same tab overwrites it; a page
 * load without one leaves it alone.
 */
export function rememberAiReferral(): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const via = detectAiReferral();
  if (!via) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, via);
  } catch {
    // Storage blocked — the click falls back to detectAiReferral().
  }
}

/** The visit's AI origin (`chatgpt`, `perplexity`, …), or null. Web only. */
export function aiReferral(): string | null {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Storage blocked — fall through.
  }
  // Still on the landing page's document (client-side navigation keeps
  // document.referrer), or storage was unavailable when it loaded.
  return detectAiReferral();
}
